/**
 * useAccessibleNavigation Hook
 *
 * Keyboard shortcuts for spatial navigation and game controls.
 *
 * Campus Overview:
 *   1-7: Navigate directly to building
 *   Enter: Enter selected/hovered building
 *
 * Inside Buildings:
 *   Escape: Return to campus
 *   Alt+1-7: Navigate to specific building
 *   Alt+0: Campus Overview
 *
 * Everywhere:
 *   Space: Toggle pause
 *   +/=: Speed up
 *   -: Speed down
 */

import { useEffect, useCallback } from 'react';
import { CampusLocation } from '../types/spatial';
import { useSpatialNavigation } from './useSpatialNavigation';
import { useNavigationModeStore } from '../stores/navigationModeStore';

const LOCATION_ORDER: CampusLocation[] = [
  CampusLocation.HEADQUARTERS,
  CampusLocation.DESIGN_STUDIO,
  CampusLocation.RD_LAB,
  CampusLocation.MANUFACTURING,
  CampusLocation.SALES_OFFICE,
  CampusLocation.EXECUTIVE_OFFICE,
  CampusLocation.COMPANY_ARCHIVES,
];

export interface UseAccessibleNavigationOptions {
  enabled?: boolean;
  onTogglePause?: () => void;
  onSpeedUp?: () => void;
  onSpeedDown?: () => void;
}

export function useAccessibleNavigation(
  options: UseAccessibleNavigationOptions = {}
): void {
  const { enabled = true, onTogglePause, onSpeedUp, onSpeedDown } = options;

  const { navigateTo, goBack, goToCampus, isUnlocked, isAtCampus } =
    useSpatialNavigation();
  const navigationMode = useNavigationModeStore((state) => state.mode);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (navigationMode !== 'spatial') return;

      // Don't handle if user is typing in an input
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Space: Toggle pause (everywhere)
      if (e.key === ' ' || e.code === 'Space') {
        e.preventDefault();
        onTogglePause?.();
        return;
      }

      // +/= : Speed up
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        onSpeedUp?.();
        return;
      }

      // - : Speed down
      if (e.key === '-') {
        e.preventDefault();
        onSpeedDown?.();
        return;
      }

      // Alt + 0: Campus Overview
      if (e.altKey && e.key === '0') {
        e.preventDefault();
        goToCampus();
        return;
      }

      // Alt + 1-7: Navigate to locations (from anywhere)
      if (e.altKey && e.key >= '1' && e.key <= '7') {
        e.preventDefault();
        const index = parseInt(e.key) - 1;
        const targetLocation = LOCATION_ORDER[index];
        if (targetLocation && isUnlocked(targetLocation)) {
          navigateTo(targetLocation);
        }
        return;
      }

      // On campus overview: bare number keys 1-7 navigate directly
      if (isAtCampus && !e.altKey && !e.ctrlKey && !e.metaKey) {
        if (e.key >= '1' && e.key <= '7') {
          e.preventDefault();
          const index = parseInt(e.key) - 1;
          const targetLocation = LOCATION_ORDER[index];
          if (targetLocation && isUnlocked(targetLocation)) {
            navigateTo(targetLocation);
          }
          return;
        }
      }

      // Escape: Go back
      if (e.key === 'Escape') {
        e.preventDefault();
        goBack();
        return;
      }
    },
    [navigationMode, navigateTo, goBack, goToCampus, isUnlocked, isAtCampus, onTogglePause, onSpeedUp, onSpeedDown]
  );

  useEffect(() => {
    if (!enabled || navigationMode !== 'spatial') return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [enabled, navigationMode, handleKeyDown]);
}

export function getKeyboardShortcuts(): Array<{ key: string; description: string }> {
  return [
    { key: 'Space', description: 'Pause / Resume' },
    { key: '+/-', description: 'Speed Up / Down' },
    { key: '1-7', description: 'Navigate to building (on campus)' },
    { key: 'Escape', description: 'Return to campus' },
    { key: 'Alt+0', description: 'Campus Overview' },
    { key: 'Alt+1-7', description: 'Jump to building (from anywhere)' },
  ];
}

export default useAccessibleNavigation;
